import { useCallback, useEffect, useState } from 'react';
import CreateIcon from '@mui/icons-material/Create';
import { useParams } from 'react-router-dom';
import DialogBox from '@/components/DialogBox';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import { JobTopic } from '@/pages/JobNotes/JobNotes.component';
import jobServices, {
  UpdateTopicName,
  NewNoteParams,
  UpdateNoteParams,
  DeleteTopicType,
  DeleteNoteParams,
} from '@/services/jobs';
import InputText from '@/trio-ui/InputText';
import Modal from '@/trio-ui/Modal';
import JobNotesCardComponent from './JobNotesCard.component';

export type JobNotesCardProps = JobTopic & {
  onAddNewNote: () => void;
};

const JobNotesCard = ({
  onAddNewNote,
  notes: originalNotes,
  ...props
}: JobNotesCardProps): JSX.Element => {
  const [newTopicToAdd, setNewTopicToAdd] = useState({
    name: props.title,
    isOpen: false,
  });
  const [indexToDelete, setIndexToDelete] = useState<number | undefined>(undefined);
  const [topictoDelete, setTopicToDelete] = useState<string>('');

  const onSelectTopicToDelete = (id: string): void => {
    setTopicToDelete(id);
  };

  const toast = useToastContext();
  const { jobId } = useParams();
  const { profile } = useProfile();
  const [newTopicNote, setNewTopicNote] = useState('');
  const [notes, setNotes] = useState(
    originalNotes.map((note) => ({
      ...note,
      isEditing: false,
    }))
  );

  const skipEditingNote = useCallback(
    (prevNotes) => {
      return originalNotes.map((originalNote) => {
        const queryNoteById = (note): boolean => note.id === originalNote.id;
        const queriedNoteById = prevNotes.find(queryNoteById);

        if (queriedNoteById?.isEditing) {
          return queriedNoteById;
        }

        return {
          ...originalNote,
          isEditing: false,
        };
      });
    },
    [originalNotes]
  );

  useEffect(() => {
    setNotes(skipEditingNote);
  }, [skipEditingNote]);

  const addNewNoteMutation = useApiSubmit<any, NewNoteParams>(jobServices.addNewNoteToTopic);
  const updateNoteMutation = useApiSubmit<any, UpdateNoteParams>(jobServices.updateNote);
  const deleteNoteMutation = useApiSubmit<any, DeleteNoteParams>(jobServices.deleteNote);

  const updateTopicMutation = useApiSubmit<any, UpdateTopicName>(jobServices.updateTopicName);
  const deleteTopicMutation = useApiSubmit<any, DeleteTopicType>(jobServices.deleteTopic);

  const onChangeNewNote = (event): void => {
    const {
      target: { value },
    } = event;
    setNewTopicNote(value);
  };

  const onSaveNote = async (index?: number): Promise<void> => {
    let isEditing = false;

    try {
      const { id: topicId } = props;
      const editedNote = notes.find((_, noteIndex) => noteIndex === index);

      if (editedNote) {
        isEditing = true;
      }

      const methodToCall = editedNote ? updateNoteMutation : addNewNoteMutation;

      const payload = {
        jobId: jobId as string,
        topicId,
        userId: profile.id as string,
        text: isEditing ? editedNote?.text : newTopicNote,
        noteId: isEditing ? editedNote?.id : '',
      };

      await methodToCall.mutateAsync(payload);

      if (isEditing) {
        setNotes((prevNotes) => {
          return prevNotes.map((note) => ({
            ...note,
            isEditing: !note?.isEditing ? false : note?.id !== payload.noteId,
          }));
        });
      }
      setNewTopicNote('');
      onAddNewNote();
      const updatedVerb = 'updated.';
      const savedVerb = 'added.';
      toast.showSuccessFeedback(`The note has been ${isEditing ? updatedVerb : savedVerb}`);
    } catch (err) {
      const updateVerb = 'updating';
      const saveVerb = 'saving';
      toast.showErrorFeedback(`There was an error ${isEditing ? updateVerb : saveVerb} the note.`);
    }
  };

  const onEditNote = (index?: number): void => {
    setNotes(
      notes.map((note, key) => {
        if (key === index) {
          return {
            ...note,
            isEditing: true,
          };
        }
        return note;
      })
    );
  };

  const onCancelEditingNote = (index?: number): void => {
    if (index === undefined) {
      setNewTopicNote('');
      return;
    }

    setNotes(
      notes.map((note, key) => {
        if (key === index) {
          return {
            ...originalNotes[index],
            isEditing: false,
          };
        }
        return note;
      })
    );
  };

  const onUpdateNote = (event: any, index?: number): void => {
    const {
      target: { value },
    } = event;

    if (index === undefined) {
      setNewTopicNote(value);
      return;
    }

    setNotes(
      notes.map((note, key) => ({
        ...note,
        text: key === index ? value : note.text,
      }))
    );
  };

  const onDeleteNote = async (index?: number): Promise<void> => {
    try {
      const noteToDelete = notes.find((note, noteIndex) => noteIndex === index);

      const payload = {
        jobId: jobId as string,
        noteId: noteToDelete ? noteToDelete?.id : '',
      };

      await deleteNoteMutation.mutateAsync(payload);
      setIndexToDelete(undefined);
      onAddNewNote();
      toast.showSuccessFeedback('The note has been deleted.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem deleting the note.');
    }
  };

  const onUpdateTopicName = async (): Promise<void> => {
    try {
      const { id: topicId } = props;

      const payload = {
        topicId,
        name: newTopicToAdd.name,
      };

      await updateTopicMutation.mutateAsync(payload);

      setNewTopicToAdd({
        isOpen: false,
        name: '',
      });
      onAddNewNote();
      toast.showSuccessFeedback('The topic has been renamed.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem renaming the topic.');
    }
  };

  const onDeleteTopic = async (): Promise<void> => {
    try {
      const { id: topicId } = props;

      const payload = {
        topicId,
      };

      await deleteTopicMutation.mutateAsync(payload);

      setTopicToDelete('');
      onAddNewNote();
      toast.showSuccessFeedback('The topic has been deleted.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem deleting the note.');
    }
  };

  const setIndexToDeleteHandler = (index?: number | undefined): void => {
    setIndexToDelete(index);
  };

  const onChangeTopicName = (event): void => {
    const {
      target: { value },
    } = event;

    setNewTopicToAdd((prev) => ({
      ...prev,
      name: value,
    }));
  };

  const onCloseModal = (): void => {
    setNewTopicToAdd({
      name: props.title,
      isOpen: false,
    });
  };

  const onOpenEditModal = (): void => {
    setNewTopicToAdd({
      name: props.title,
      isOpen: true,
    });
  };

  const isSubmiting =
    addNewNoteMutation.isLoading || updateNoteMutation.isLoading || deleteNoteMutation.isLoading;

  return (
    <>
      <JobNotesCardComponent
        onSelectTopicToDelete={onSelectTopicToDelete}
        isSubmiting={isSubmiting}
        userName={profile?.name ?? ''}
        onSaveNote={onSaveNote}
        onChangeHandler={onChangeNewNote}
        onEditNote={onEditNote}
        newTopicNote={newTopicNote}
        notes={notes}
        onUpdateNote={onUpdateNote}
        onDeleteNote={setIndexToDeleteHandler}
        onCancelEditingNote={onCancelEditingNote}
        onOpenEditModal={onOpenEditModal}
        {...props}
      />

      <Modal
        modalId="new-topic-name"
        isCentered
        size="sm"
        headerIcon={CreateIcon}
        onConfirmClick={onUpdateTopicName}
        modalTitle="Type a new name"
        onConfirmLabel="Save"
        isButtonDisabled={!newTopicToAdd.name}
        bodyRender={
          <InputText
            name="newtopic"
            placeholder=""
            value={newTopicToAdd.name}
            onChangeHandler={onChangeTopicName}
          />
        }
        onClose={onCloseModal}
        isOpen={newTopicToAdd.isOpen}
      />

      <DialogBox
        bodyText="Any notes inside this topic will be also deleted. This action cannot be undone."
        data-testid="delete-topic-dialog"
        isOpen={!!topictoDelete}
        onCancelHandler={() => setTopicToDelete('')}
        onConfirmHandler={() => onDeleteTopic()}
      />
      <DialogBox
        data-testid="delete-note-dialog"
        isOpen={indexToDelete != null}
        onCancelHandler={() => setIndexToDeleteHandler(undefined)}
        onConfirmHandler={() => onDeleteNote(indexToDelete)}
      />
    </>
  );
};

export default JobNotesCard;
